import { useState } from "react";
import { BookOpen, Search, Loader2 } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const EXAMPLES = ["John 3:16", "Psalm 23", "Isaiah 54:17", "Jeremiah 29:11", "Philippians 4:13"];

export const BibleSearch = () => {
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (query) => {
    const term = (query ?? q).trim();
    if (!term) return;
    setQ(term);
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const res = await fetch(`https://bible-api.com/${encodeURIComponent(term)}?translation=kjv`);
      if (!res.ok) throw new Error("not found");
      const data = await res.json();
      if (!data.verses || !data.verses.length) throw new Error("not found");
      setResult(data);
    } catch {
      setErr("Could not find that reference. Try e.g. “John 3:16”, “Psalm 23”, or “Romans 8”.");
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    search();
  };

  return (
    <section
      id="bible"
      data-testid="bible-section"
      className="relative py-24 sm:py-32 bg-parchment-alt dark:bg-[#0B0F19]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3">
            <BookOpen className="text-gold-deep dark:text-gold-light" size={20} />
            <Overline>KJV Scripture Search</Overline>
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            Search the Word of God.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Look up any chapter or verse in the King James Version. Enter a
            reference such as a book, chapter, and verse.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="bible-form" className="mt-8 flex gap-3">
            <input
              data-testid="bible-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. John 3:16 or Psalm 23"
              className="flex-1 px-4 py-3 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              data-testid="bible-search-button"
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold hover:bg-gold hover:text-ink dark:hover:bg-gold-light transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              Search
            </button>
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => search(ex)}
                className="px-3 py-1.5 rounded-full border border-border text-xs font-medium hover:border-gold transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>
        </Reveal>

        {err && (
          <p data-testid="bible-error" className="mt-6 text-sm text-red-500">{err}</p>
        )}

        {result && (
          <Reveal>
            <div data-testid="bible-result" className="mt-8 p-7 sm:p-9 rounded-3xl bg-ink text-parchment grain relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-70"
                style={{ background: "radial-gradient(circle at 85% 15%, rgba(197,160,89,0.16), transparent 45%)" }}
              />
              <h3 className="relative font-serif text-2xl font-semibold gold-foil">{result.reference}</h3>
              <div className="relative mt-4 space-y-2 leading-relaxed text-parchment/90">
                {result.verses.map((v, i) => (
                  <p key={i}>
                    <sup className="text-gold-light mr-1 font-mono">{v.verse}</sup>
                    {v.text.trim()}
                  </p>
                ))}
              </div>
              <p className="relative mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/50">
                {result.translation_name || "King James Version"}
              </p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
